import { z } from 'zod';

export const registrationSelectionTypeSchema = z.enum(['SINGLE', 'MULTI']);
export type RegistrationSelectionType = z.infer<typeof registrationSelectionTypeSchema>;

/** Auswahlen einer Person: Bereichs-id (als String, wie JSON-Objektschluessel) -> Options-ids. */
export const registrationSelectionsSchema = z.record(z.string(), z.array(z.number().int()));
export type IRegistrationSelections = z.infer<typeof registrationSelectionsSchema>;

export const registrationOptionSchema = z.object({
    id: z.number().int(),
    label: z.string(),
    sort_order: z.number().int().nonnegative().default(0),
});
export type IRegistrationOption = z.infer<typeof registrationOptionSchema>;

export const registrationFieldSchema = z.object({
    id: z.number().int(),
    label: z.string(),
    selection_type: registrationSelectionTypeSchema,
    sort_order: z.number().int().nonnegative().default(0),
    options: z.array(registrationOptionSchema).default([]),
});
export type IRegistrationField = z.infer<typeof registrationFieldSchema>;

export const registrationFormSchema = z.object({
    id: z.number().int(),
    structure_version: z.number().int(),
    comment_enabled: z.boolean(),
    comment_label: z.string().default('Kommentar / Hinweis'),
    fields: z.array(registrationFieldSchema).default([]),
});
export type IRegistrationForm = z.infer<typeof registrationFormSchema>;

export const registrationFormResponseSchema = z.object({
    event_id: z.number().int(),
    form: registrationFormSchema.nullable(),
    answer_count: z.number().int().nonnegative().default(0),
});
export type IRegistrationFormResponse = z.infer<typeof registrationFormResponseSchema>;

export const participantKindSchema = z.enum(['MEMBER', 'GUEST']);
export type ParticipantKind = z.infer<typeof participantKindSchema>;

/** Gespeicherter Teilnehmer, wie das Backend ihn liefert. */
export const rsvpParticipantSchema = z.object({
    id: z.number().int().nullable().optional().default(null),
    kind: participantKindSchema,
    name: z.string().default(''),
    display_name: z.string().default(''),
    comment: z.string().default(''),
    sort_order: z.number().int().nonnegative().default(0),
    selections: registrationSelectionsSchema.default({}),
});
export type IRsvpParticipant = z.infer<typeof rsvpParticipantSchema>;

/** Teilnehmer im Schreib-Payload -- ohne ids, da das Backend die Liste ersetzt (D4). */
export const participantDraftSchema = z.object({
    kind: participantKindSchema,
    name: z.string().max(150).default(''),
    comment: z.string().max(2000).default(''),
    selections: registrationSelectionsSchema.default({}),
});
export type IParticipantDraft = z.infer<typeof participantDraftSchema>;

/** Entwurfszustand des Builders: neue Bereiche/Optionen haben noch keine id. */
export const registrationOptionDraftSchema = z.object({
    id: z.number().int().optional(),
    label: z.string().max(200),
});
export type IRegistrationOptionDraft = z.infer<typeof registrationOptionDraftSchema>;

export const registrationFieldDraftSchema = z.object({
    id: z.number().int().optional(),
    label: z.string().max(120),
    selection_type: registrationSelectionTypeSchema,
    options: z.array(registrationOptionDraftSchema),
});
export type IRegistrationFieldDraft = z.infer<typeof registrationFieldDraftSchema>;

export const registrationFormWriteSchema = z.object({
    comment_enabled: z.boolean(),
    comment_label: z.string().max(120),
    fields: z.array(registrationFieldDraftSchema),
    confirm_discard_answers: z.boolean().optional(),
});
export type IRegistrationFormWrite = z.infer<typeof registrationFormWriteSchema>;

export const registrationAggregationFieldSchema = z.object({
    field_id: z.number().int(),
    label: z.string(),
    selection_type: registrationSelectionTypeSchema,
    options: z.array(
        z.object({
            option_id: z.number().int(),
            label: z.string(),
            count: z.number().int().nonnegative(),
        })
    ).default([]),
});
export type IRegistrationAggregationField = z.infer<typeof registrationAggregationFieldSchema>;

export const registrationOverviewSchema = z.object({
    event_id: z.number().int(),
    event_title: z.string().default(''),
    form: registrationFormSchema.nullable(),
    totals: z.object({
        going: z.number().int().nonnegative().default(0),
        guests: z.number().int().nonnegative().default(0),
        attendees: z.number().int().nonnegative().default(0),
    }),
    aggregation: z.array(registrationAggregationFieldSchema).default([]),
    registrations: z.array(
        z.object({
            rsvp_id: z.number().int(),
            member: z.object({ id: z.number().int(), name: z.string() }),
            guest_count: z.number().int().nonnegative().default(0),
            participants: z.array(rsvpParticipantSchema).default([]),
        })
    ).default([]),
});
export type IRegistrationOverview = z.infer<typeof registrationOverviewSchema>;
